import React, { useEffect, useState } from "react";
import AppShell from "../components/AppShell";
import { api } from "../lib/api";
import { toast } from "sonner";
import { FloppyDisk, CheckCircle, WarningCircle } from "@phosphor-icons/react";

export default function Settings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/settings").then(r => setS(r.data)).catch(() => toast.error("Failed to load")); }, []);

  const save = async () => {
    if (!s) return;
    setSaving(true);
    try {
      const r = await api.patch("/settings", {
        private_handoff_email: s.private_handoff_email,
        alert_phone_number: s.alert_phone_number,
        max_price: Number(s.max_price),
        min_equity: Number(s.min_equity),
        min_profit: Number(s.min_profit),
        target_states: (typeof s.target_states === "string" ? s.target_states.split(",").map(x => x.trim().toUpperCase()).filter(Boolean) : s.target_states),
        target_property_types: s.target_property_types,
        fsbo_only: !!s.fsbo_only,
        require_skip_trace: !!s.require_skip_trace,
        demo_fallback_enabled: !!s.demo_fallback_enabled,
        auto_scan_enabled: !!s.auto_scan_enabled,
        buyer_entity_name: s.buyer_entity_name,
        title_company_name: s.title_company_name,
        title_company_email: s.title_company_email,
        bank_details: s.bank_details,
        default_assignment_fee: s.default_assignment_fee != null ? Number(s.default_assignment_fee) : undefined,
        business_address: s.business_address,
        business_city: s.business_city,
        business_state: s.business_state,
        business_zip: s.business_zip,
        daily_send_limit: s.daily_send_limit != null ? Number(s.daily_send_limit) : undefined,
      });
      setS(r.data);
      toast.success("Settings saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  if (!s) return <AppShell header={null}><div className="p-6 text-sm text-zinc-500">Loading…</div></AppShell>;

  const targetStatesStr = Array.isArray(s.target_states) ? s.target_states.join(", ") : s.target_states;

  return (
    <AppShell header={<div className="border-b border-zinc-800 px-6 py-4"><div className="overline">Settings</div><h1 className="text-lg font-semibold">Engine configuration</h1></div>}>
      <div className="p-6 max-w-3xl">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
          <StatusPill label="BatchData API" ok={true} note="Server token configured" />
          <StatusPill label="Resend Email" ok={s.resend_api_key_present} note={s.resend_api_key_present ? "Live sending" : "Mock mode — add RESEND_API_KEY"} />
          <StatusPill label="Twilio SMS" ok={s.twilio_configured} note={s.twilio_configured ? "Live SMS alerts" : "Mock mode — add TWILIO_* env vars"} />
        </div>

        <section className="border border-zinc-800 rounded-sm p-5 space-y-4" data-testid="settings-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Private Handoff Email" hint="Where HOT DEAL alerts get forwarded">
              <input data-testid="settings-handoff-email" value={s.private_handoff_email || ""} onChange={e => setS({ ...s, private_handoff_email: e.target.value })}
                className="w-full bg-[#18181b] hairline px-3 py-2 mono text-sm rounded-sm focus:outline-none focus:border-emerald-500" />
            </Field>
            <Field label="SMS Alert Number" hint="E.164 format · e.g. +15551234567">
              <input data-testid="settings-phone" value={s.alert_phone_number || ""} onChange={e => setS({ ...s, alert_phone_number: e.target.value })}
                placeholder="+1..."
                className="w-full bg-[#18181b] hairline px-3 py-2 mono text-sm rounded-sm focus:outline-none focus:border-emerald-500" />
            </Field>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Max Price ($)" hint="Cap on ARV">
              <input data-testid="settings-max-price" type="number" value={s.max_price ?? 0} onChange={e => setS({ ...s, max_price: e.target.value })}
                className="w-full bg-[#18181b] hairline px-3 py-2 mono text-sm rounded-sm focus:outline-none focus:border-emerald-500" />
            </Field>
            <Field label="Min Equity (%)" hint="Floor on owner equity">
              <input data-testid="settings-min-equity" type="number" value={s.min_equity ?? 0} onChange={e => setS({ ...s, min_equity: e.target.value })}
                className="w-full bg-[#18181b] hairline px-3 py-2 mono text-sm rounded-sm focus:outline-none focus:border-emerald-500" />
            </Field>
            <Field label="Min Profit ($)" hint="Lead Auditor: silently drops leads below this">
              <input data-testid="settings-min-profit" type="number" value={s.min_profit ?? 15000} onChange={e => setS({ ...s, min_profit: e.target.value })}
                className="w-full bg-[#18181b] hairline px-3 py-2 mono text-sm rounded-sm focus:outline-none focus:border-emerald-500" />
            </Field>
          </div>
          <Field label="Target States" hint={`${(Array.isArray(s.target_states) ? s.target_states.length : (s.target_states||'').split(',').length)} states — comma-separated`}>
            <input data-testid="settings-states" value={targetStatesStr} onChange={e => setS({ ...s, target_states: e.target.value })}
              className="w-full bg-[#18181b] hairline px-3 py-2 mono text-sm rounded-sm focus:outline-none focus:border-emerald-500" />
          </Field>

          <div>
            <div className="overline mb-2">Property Types</div>
            <div className="flex flex-wrap gap-2">
              {["Land", "Residential", "Commercial"].map(t => {
                const on = (s.target_property_types || []).includes(t);
                return (
                  <button key={t} type="button" data-testid={`settings-ptype-${t.toLowerCase()}`}
                    onClick={() => {
                      const cur = new Set(s.target_property_types || []);
                      if (on) cur.delete(t); else cur.add(t);
                      setS({ ...s, target_property_types: Array.from(cur) });
                    }}
                    className={`text-[11px] mono uppercase tracking-[0.15em] px-2.5 py-1.5 rounded-sm border transition-colors ${on ? "bg-emerald-500/15 border-emerald-500/50 text-emerald-300" : "border-zinc-800 text-zinc-500 hover:text-zinc-200 hover:border-zinc-600"}`}>
                    {t}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="flex items-center gap-2 text-sm text-zinc-300 select-none">
              <input data-testid="settings-fsbo" type="checkbox" checked={!!s.fsbo_only} onChange={e => setS({ ...s, fsbo_only: e.target.checked })} />
              FSBO only <span className="text-[10px] mono text-zinc-500">(For-Sale-By-Owner)</span>
            </label>
            <label className="flex items-center gap-2 text-sm text-zinc-300 select-none">
              <input data-testid="settings-skip-trace" type="checkbox" checked={!!s.require_skip_trace} onChange={e => setS({ ...s, require_skip_trace: e.target.checked })} />
              Require BatchData skip-trace email
            </label>
            <label className="flex items-center gap-2 text-sm text-zinc-300 select-none">
              <input data-testid="settings-autoscan" type="checkbox" checked={!!s.auto_scan_enabled} onChange={e => setS({ ...s, auto_scan_enabled: e.target.checked })} />
              Auto daily scan (08:00 UTC)
            </label>
            <label className="flex items-center gap-2 text-sm text-zinc-500 select-none">
              <input data-testid="settings-demo" type="checkbox" checked={!!s.demo_fallback_enabled} onChange={e => setS({ ...s, demo_fallback_enabled: e.target.checked })} />
              <span>Demo fallback <span className="text-[10px] mono">(seed synthetic leads if BatchData is empty)</span></span>
            </label>
          </div>

          <div className="border-t border-zinc-800 pt-4">
            <div className="overline mb-3">Closing &amp; Title Handoff</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Buyer Entity" hint="Buyer name on all Purchase Agreements">
                <input data-testid="settings-buyer-entity" value={s.buyer_entity_name || ""} onChange={e => setS({ ...s, buyer_entity_name: e.target.value })}
                  className="w-full bg-[#18181b] hairline px-3 py-2 mono text-sm rounded-sm focus:outline-none focus:border-emerald-500" />
              </Field>
              <Field label="Title Company Name">
                <input data-testid="settings-title-name" value={s.title_company_name || ""} onChange={e => setS({ ...s, title_company_name: e.target.value })}
                  className="w-full bg-[#18181b] hairline px-3 py-2 mono text-sm rounded-sm focus:outline-none focus:border-emerald-500" />
              </Field>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <Field label="Title Company Email" hint="Settlement packages get emailed here">
                <input data-testid="settings-title-email" type="email" value={s.title_company_email || ""} onChange={e => setS({ ...s, title_company_email: e.target.value })}
                  className="w-full bg-[#18181b] hairline px-3 py-2 mono text-sm rounded-sm focus:outline-none focus:border-emerald-500" />
              </Field>
              <Field label="Default Assignment Fee ($)">
                <input data-testid="settings-assign-fee" type="number" value={s.default_assignment_fee ?? 10000} onChange={e => setS({ ...s, default_assignment_fee: e.target.value })}
                  className="w-full bg-[#18181b] hairline px-3 py-2 mono text-sm rounded-sm focus:outline-none focus:border-emerald-500" />
              </Field>
            </div>
            <Field label="Wire / Bank Details" hint="Sent to title company at settlement — kept in DB, never in seller-facing emails">
              <textarea data-testid="settings-bank" rows={4} value={s.bank_details || ""} onChange={e => setS({ ...s, bank_details: e.target.value })}
                placeholder="Bank: ...&#10;Routing: ...&#10;Account: ...&#10;Beneficiary: Jack Royals LLC"
                className="w-full bg-[#18181b] hairline px-3 py-2 mono text-xs rounded-sm focus:outline-none focus:border-emerald-500" />
            </Field>
          </div>

          <div className="border-t border-zinc-800 pt-4">
            <div className="overline mb-1">CAN-SPAM Compliance</div>
            <div className="text-[11px] text-zinc-500 mb-3 mono">Physical mailing address is legally required in every commercial email footer. Every outbound offer/follow-up will include this address + a one-click unsubscribe link tied to that lead.</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Business Street Address" hint="e.g. 12345 Ranch Rd Ste 100">
                <input data-testid="settings-biz-address" value={s.business_address || ""} onChange={e => setS({ ...s, business_address: e.target.value })}
                  className="w-full bg-[#18181b] hairline px-3 py-2 mono text-sm rounded-sm focus:outline-none focus:border-emerald-500" />
              </Field>
              <Field label="City">
                <input data-testid="settings-biz-city" value={s.business_city || ""} onChange={e => setS({ ...s, business_city: e.target.value })}
                  className="w-full bg-[#18181b] hairline px-3 py-2 mono text-sm rounded-sm focus:outline-none focus:border-emerald-500" />
              </Field>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <Field label="State">
                <input data-testid="settings-biz-state" value={s.business_state || ""} onChange={e => setS({ ...s, business_state: e.target.value.toUpperCase() })}
                  className="w-full bg-[#18181b] hairline px-3 py-2 mono text-sm rounded-sm focus:outline-none focus:border-emerald-500" />
              </Field>
              <Field label="ZIP">
                <input data-testid="settings-biz-zip" value={s.business_zip || ""} onChange={e => setS({ ...s, business_zip: e.target.value })}
                  className="w-full bg-[#18181b] hairline px-3 py-2 mono text-sm rounded-sm focus:outline-none focus:border-emerald-500" />
              </Field>
              <Field label="Daily Send Limit" hint="Max cold emails per UTC day (T&amp;S compliance)">
                <input data-testid="settings-daily-limit" type="number" min="0" value={s.daily_send_limit ?? 50} onChange={e => setS({ ...s, daily_send_limit: e.target.value })}
                  className="w-full bg-[#18181b] hairline px-3 py-2 mono text-sm rounded-sm focus:outline-none focus:border-emerald-500" />
              </Field>
            </div>
          </div>
          <div>
            <button onClick={save} disabled={saving} data-testid="settings-save"
              className="text-xs mono uppercase tracking-[0.15em] px-3 py-2 rounded-sm bg-emerald-500 hover:bg-emerald-400 text-black font-semibold flex items-center gap-2 disabled:opacity-50">
              <FloppyDisk size={13} weight="bold" /> {saving ? "Saving…" : "Save Settings"}
            </button>
          </div>
        </section>

        <div className="mt-6 text-xs text-zinc-500 mono border border-zinc-800/70 p-4 rounded-sm space-y-2">
          <div className="overline">Autopilot Protocols</div>
          <div>· <span className="text-emerald-300">Automation Disclosure</span> — every automated correspondence opens with a visible banner stating the message is generated by an automated system; sender name is "{s.buyer_entity_name || 'Jack Royals LLC'} — Automated Correspondence".</div>
          <div>· <span className="text-emerald-300">Reply Handling Disclosure</span> — original email and auto-acknowledgment both tell the recipient replies are processed by an automated system before a human sees them.</div>
          <div>· <span className="text-emerald-300">Honest Subject Lines</span> — no rotation. Every follow-up uses "Follow-up on our automated cash offer — {'{address}'}".</div>
          <div>· <span className="text-zinc-300">Buyer Entity</span> — every offer &amp; contract signed as "{s.buyer_entity_name || 'Jack Royals LLC'} <em>and/or assigns</em>"</div>
          <div>· <span className="text-zinc-300">Scan Filter</span> — {(s.target_states||[]).join(', ') || '—'} · {(s.target_property_types||[]).join(' + ') || 'all types'} {s.fsbo_only ? '· FSBO only' : ''}</div>
          <div>· <span className="text-zinc-300">Live Outreach Guard</span> — {s.require_skip_trace ? 'ONLY sends when BatchData returns a verified owner email' : 'sends even without an email on file'}</div>
          <div>· <span className="text-zinc-300">Lead Auditor</span> — MAO = 70% of ARV. Deals below ${Number(s.min_profit || 0).toLocaleString()} projected profit are silently archived.</div>
          <div>· <span className="text-zinc-300">Autonomous Follow-ups</span> — every 3 days, 3 rounds max, same honest subject line each round.</div>
          <div>· <span className="text-zinc-300">'Yes' Trigger</span> — full thread forwarded + SMS ping + Purchase Agreement auto-generated &amp; sent.</div>
          <div>· <span className="text-zinc-300">Title Handoff</span> — both docs signed → settlement package emailed to title with wire details automatically.</div>
        </div>

        <div className="mt-6 border border-red-500/30 bg-red-500/5 rounded-sm p-4">
          <div className="overline text-red-400 mb-1">Danger Zone</div>
          <div className="text-xs text-zinc-400 mb-3">Purge all leads, hot deals, emails, handoffs, scan logs, and documents. Settings are preserved.</div>
          <button data-testid="btn-purge"
            onClick={async () => {
              const answer = window.prompt('Type PURGE to wipe demo-only leads (real BatchData leads are safe). Add "NUKE" after to wipe EVERYTHING:');
              if (!answer || !answer.startsWith('PURGE')) { toast.info('Cancelled'); return; }
              const nuke = answer.toUpperCase().includes('NUKE');
              try {
                const r = await api.post('/admin/purge', { confirm: 'PURGE', keep_settings: true, demo_only: !nuke, nuke_real: nuke });
                const c = r.data.purged;
                toast.success(`${r.data.scope} · wiped ${c.leads} leads, ${c.emails} emails, ${c.handoffs || 0} handoffs, ${c.documents || 0} docs`);
              } catch (e) { toast.error(e?.response?.data?.detail || 'Purge failed'); }
            }}
            className="text-xs mono uppercase tracking-[0.15em] px-3 py-2 rounded-sm border border-red-500/60 bg-red-500/10 hover:bg-red-500/20 text-red-300">
            Purge Dashboard
          </button>
        </div>
      </div>
    </AppShell>
  );
}

const Field = ({ label, hint, children }) => (
  <div>
    <div className="overline mb-1">{label}</div>
    {children}
    {hint && <div className="text-[10px] mono text-zinc-500 mt-1">{hint}</div>}
  </div>
);

const StatusPill = ({ label, ok, note }) => (
  <div className={`border rounded-sm p-3 flex items-start gap-2 ${ok ? "border-emerald-500/30 bg-emerald-500/5" : "border-amber-500/30 bg-amber-500/5"}`}>
    {ok ? <CheckCircle size={18} weight="fill" className="text-emerald-400 mt-0.5" /> : <WarningCircle size={18} weight="fill" className="text-amber-400 mt-0.5" />}
    <div>
      <div className="text-sm font-medium">{label}</div>
      <div className="text-[11px] mono text-zinc-500">{note}</div>
    </div>
  </div>
);
